import { useEffect, useRef, useState } from 'react';

import type { IAutoCompleteOption } from './autocomplete';

const KEY_CODES = {
  DOWN: 40,
  UP: 38,
  PAGE_DOWN: 34,
  ESCAPE: 27,
  PAGE_UP: 33,
  ENTER: 13
};

interface IProps {
  value: string;
  lastValue: string;
  onChange: (value: string, option?: IAutoCompleteOption | undefined) => void;
  defaultOptions: IAutoCompleteOption[];
  delay: number;
  source: (search: string) => IAutoCompleteOption[];
  handleFocus: (value: string) => void;
  handleBlur: (value: string) => void;
}

const useAutoComplete = ({
  value,
  lastValue,
  onChange,
  defaultOptions,
  delay,
  source,
  handleFocus,
  handleBlur
}: IProps) => {
  const listRef = useRef<HTMLUListElement>(null);

  const [suggestions, setSuggestions] = useState<IAutoCompleteOption[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isBusy, setBusy] = useState(false);
  const selectionArray =
    suggestions.length > 0 ? suggestions : value === '' ? defaultOptions : [];

  const [myTimeout, setMyTimeOut] = useState(setTimeout(() => {}, 0));

  useEffect(() => {
    const selectedItem = selectionArray.findIndex(
      (item) => item.label === lastValue
    );
    if (selectedItem !== -1) {
      setSelectedIndex(selectedItem);
    }
  }, [lastValue]);

  useEffect(() => {
    if (listRef.current && selectedIndex !== -1) {
      listRef.current.scrollTop =
        (listRef.current.children[selectedIndex] as HTMLElement)?.offsetTop -
        listRef.current.children[selectedIndex].clientHeight;
    }
  }, [selectedIndex]);

  function delayInvoke(cb: () => void) {
    if (myTimeout) {
      clearTimeout(myTimeout);
    }
    setMyTimeOut(setTimeout(cb, delay));
  }

  function clearSuggestions() {
    setSuggestions([]);
    setSelectedIndex(-1);
  }

  function selectOption(index: number) {
    if (index > -1) {
      onChange(selectionArray[index].label, selectionArray[index]);
    }
    handleBlur(selectionArray[index]?.label);
    clearSuggestions();
  }

  async function getSuggestions(searchTerm: string) {
    if (searchTerm && source) {
      const options = await source(searchTerm);
      handleFocus(searchTerm);
      setSuggestions(options);
    }
  }

  function onTextChange(searchTerm: string) {
    setBusy(true);
    onChange(
      searchTerm,
      defaultOptions.find((item) => item.label === searchTerm)
    );
    clearSuggestions();
    delayInvoke(() => {
      getSuggestions(searchTerm);
      setBusy(false);
    });
  }

  function scrollUp() {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    } else if (selectedIndex === -1 || selectedIndex === 0) {
      setSelectedIndex(selectionArray.length - 1);
    }
  }

  function scrollDown() {
    if (selectedIndex < selectionArray.length - 1 || selectedIndex === -1) {
      setSelectedIndex(selectedIndex + 1);
    } else if (selectedIndex === selectionArray.length - 1) {
      setSelectedIndex(0);
    }
  }

  function pageDown() {
    setSelectedIndex(selectionArray.length - 1);
  }

  function pageUp() {
    setSelectedIndex(0);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const keyOperation = {
      [KEY_CODES.DOWN]: scrollDown,
      [KEY_CODES.UP]: scrollUp,
      [KEY_CODES.ENTER]: () => selectOption(selectedIndex),
      [KEY_CODES.ESCAPE]: clearSuggestions,
      [KEY_CODES.PAGE_DOWN]: pageDown,
      [KEY_CODES.PAGE_UP]: pageUp
    };
    if (keyOperation[e.keyCode]) {
      keyOperation[e.keyCode]();
    } else {
      setSelectedIndex(-1);
    }
  }

  return {
    bindOption: {
      onClick: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        const nodes = Array.from(listRef.current?.children || []);
        // @ts-expect-error - closest is not a property of EventTarget
        selectOption(nodes.indexOf(e.target.closest('li')));
      }
    },
    bindInput: {
      value,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        onTextChange(e.target.value),
      onKeyDown
    },
    bindOptions: {
      ref: listRef
    },
    isBusy,
    suggestions: selectionArray,
    selectedIndex,
    clearSuggestions
  };
};

export default useAutoComplete;
